import { Link } from 'react-router-dom';
import usePageMeta from '../../hooks/usePageMeta';
import { ArrowRight } from 'lucide-react';

export default function AboutPage() {
  usePageMeta({
    title: 'About Stratel – Healthcare Strategy & Operations',
    description: 'Learn about Stratel’s mission, vision, and values. We specialise in long-term care strategy, compliance, and operational excellence.',
    keywords: 'about Stratel, healthcare consultants, long-term care , mission vision values',
  });
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-secondary-900 via-secondary-800 to-primary-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-display font-bold">
              About Us
            </h1>
            <p className="mt-6 text-xl text-secondary-300">
              Dedicated to helping organizations achieve excellence through 
              strategic guidance and operational expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-display font-bold text-secondary-900">
                Our Mission
              </h2>
              <p className="mt-6 text-lg text-secondary-600 leading-relaxed">
                We are committed to empowering long-term care organizations with the strategic insights and operational tools they need to 
                thrive in an ever-changing landscape.
                {/* healthcare organizations, public health agencies, and businesses */}
              </p>
              <p className="mt-4 text-lg text-secondary-600 leading-relaxed">
                Our team brings together experience across healthcare administration, 
                public health policy, strategic planning, and organizational development to 
                support meaningful progress.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-video shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=450&fit=crop" 
                alt="Healthcare leaders meeting with consultants"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden aspect-video shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=450&fit=crop"
                alt="Leadership team planning together"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-display font-bold text-secondary-900">
                Our Vision
              </h2>
              <p className="mt-6 text-lg text-secondary-600 leading-relaxed">
                We envision resilient care organizations that lead with clarity, use data with confidence,
                and build teams that thrive.
              </p>
              <p className="mt-4 text-lg text-secondary-600 leading-relaxed">
                By aligning strategy with day-to-day execution, we help leaders create sustainable,
                people-centered outcomes that endure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-display font-bold text-secondary-900 text-center">
            Our Values
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Excellence',
                description: 'We hold ourselves to the highest standards in everything we do.',
              },
              {
                title: 'Partnership',
                description: 'We work alongside our clients as true partners in their success.',
              },
              {
                title: 'Impact',
                description: 'We focus on practical work that helps organizations move toward their goals.',
              },
            ].map((value) => (
              <div key={value.title} className="card p-8 text-center">
                <h3 className="text-xl font-semibold text-secondary-900">
                  {value.title}
                </h3>
                <p className="mt-4 text-secondary-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-display font-bold text-white">
            Ready to Work Together?
          </h2>
          <p className="mt-4 text-xl text-primary-100">
            Let's discuss how we can help your organization succeed.
          </p>
          <Link
            to="/contact"
            className="btn bg-white text-primary-600 hover:bg-primary-50 btn-lg mt-8 inline-flex"
          >
            Get in Touch
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
